import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

// Screen-specific quick actions configuration
const SCREEN_ACTIONS = {
  '/dashboard-overview': [
    { label: 'Refresh Data', icon: 'RefreshCw', variant: 'outline', action: 'refresh' },
    { label: 'Export Report', icon: 'Download', variant: 'outline', action: 'export' },
  ],
  '/account-management': [
    { label: 'Add Account', icon: 'UserPlus', variant: 'default', action: 'add-account', primary: true },
    { label: 'Import Accounts', icon: 'Upload', variant: 'outline', action: 'import' },
    { label: 'Export List', icon: 'Download', variant: 'outline', action: 'export' },
  ],
  '/sales-ordering-system': [
    { label: 'New Order', icon: 'Plus', variant: 'default', action: 'new-order', primary: true },
    { label: 'Bulk Import', icon: 'Upload', variant: 'outline', action: 'bulk-import' },
    { label: 'Export Orders', icon: 'Download', variant: 'outline', action: 'export' },
  ],
  '/performance-analytics': [
    { label: 'Export Report', icon: 'Download', variant: 'outline', action: 'export' },
    { label: 'Schedule Report', icon: 'Calendar', variant: 'outline', action: 'schedule' },
    { label: 'Share Insights', icon: 'Share2', variant: 'outline', action: 'share' },
  ],
  '/ai-intelligence-reports': [
    { label: 'Generate Report', icon: 'BrainCircuit', variant: 'default', action: 'generate', primary: true },
    { label: 'Export PDF', icon: 'FileText', variant: 'outline', action: 'export-pdf' },
  ],
  '/philippine-map-visualization': [
    { label: 'Export Map', icon: 'Download', variant: 'outline', action: 'export-map' },
    { label: 'Filter Regions', icon: 'Filter', variant: 'outline', action: 'filter' },
  ],
};

// Screens where toolbar should be shown
const TOOLBAR_SCREENS = Object.keys(SCREEN_ACTIONS);

export default function QuickActionToolbar({ onAction }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [fabOpen, setFabOpen] = useState(false);

  const currentActions = SCREEN_ACTIONS?.[location?.pathname];

  // Don't render if no actions for current screen
  if (!currentActions || !TOOLBAR_SCREENS?.includes(location?.pathname)) {
    return null;
  }

  const primaryAction = currentActions?.find((a) => a?.primary);
  const secondaryActions = currentActions?.filter((a) => !a?.primary);

  const handleAction = (action) => {
    setFabOpen(false);
    if (onAction) onAction(action);
  };

  return (
    <>
      {/* Desktop Toolbar */}
      <div
        className="hidden md:flex items-center gap-3 px-6 py-3 bg-card border-b border-border"
        role="toolbar"
        aria-label="Quick actions"
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          {currentActions?.map((action) => (
            <Button
              key={action?.action}
              variant={action?.variant}
              size="sm"
              iconName={action?.icon}
              iconPosition="left"
              iconSize={15}
              onClick={() => handleAction(action?.action)}
              className="press-effect"
            >
              {action?.label}
            </Button>
          ))}
        </div>

        {/* Breadcrumb-style context indicator */}
        <div className="flex items-center gap-2 text-muted-foreground font-caption text-xs flex-shrink-0">
          <Icon name="Clock" size={12} color="currentColor" strokeWidth={2} />
          <span>Updated: {new Date()?.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
      {/* Mobile FAB */}
      {primaryAction && (
        <div className="md:hidden fixed bottom-20 right-4 z-[90]">
          {/* Secondary actions (expanded) */}
          {fabOpen && (
            <div className="absolute bottom-16 right-0 flex flex-col gap-2 items-end animate-fade-in">
              {secondaryActions?.map((action) => (
                <button
                  key={action?.action}
                  className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-md shadow-lg font-caption text-sm text-card-foreground transition-all duration-200 hover:bg-muted press-effect min-h-[44px]"
                  onClick={() => handleAction(action?.action)}
                >
                  <Icon name={action?.icon} size={16} color="currentColor" strokeWidth={2} />
                  <span>{action?.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* FAB backdrop */}
          {fabOpen && (
            <div
              className="fixed inset-0 z-[-1]"
              onClick={() => setFabOpen(false)}
              aria-hidden="true"
            />
          )}

          {/* Primary FAB button */}
          <button
            className="flex items-center justify-center w-14 h-14 rounded-full shadow-xl text-primary-foreground transition-all duration-200 press-effect focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            style={{ background: 'var(--color-primary)' }}
            onClick={() => {
              if (secondaryActions?.length > 0) {
                setFabOpen((p) => !p);
              } else {
                handleAction(primaryAction?.action);
              }
            }}
            aria-label={primaryAction?.label}
            aria-expanded={fabOpen}
          >
            <Icon
              name={fabOpen ? 'X' : primaryAction?.icon}
              size={24}
              color="#FFFFFF"
              strokeWidth={2}
              className="transition-transform duration-200"
            />
          </button>
        </div>
      )}
    </>
  );
}